// MLS Field Mapping for AI Natural Language Processing
// This file maps natural language terms to ARMLS field names and search criteria

export const MLSFieldMapping = {
  // Price-related fields
  price: {
    fields: ['ListPrice', 'CurrentPrice'],
    aliases: ['price', 'cost', 'asking price', 'list price'],
    operators: {
      'under': 'maxPrice',
      'below': 'maxPrice',
      'less than': 'maxPrice',
      'max': 'maxPrice',
      'maximum': 'maxPrice',
      'over': 'minPrice',
      'above': 'minPrice',
      'more than': 'minPrice',
      'min': 'minPrice',
      'minimum': 'minPrice',
      'between': 'range'
    }
  },

  // Property characteristics
  bedrooms: {
    fields: ['BedroomsTotal', 'BedsTotal'],
    aliases: ['bed', 'bedroom', 'bedrooms', 'br', 'beds'],
    operators: {
      'at least': 'min',
      'minimum': 'min',
      'exactly': 'exact',
      'or more': 'min'
    }
  },

  bathrooms: {
    fields: ['BathroomsTotalDecimal', 'BathroomsTotalInteger', 'BathsTotal'],
    aliases: ['bath', 'bathroom', 'bathrooms', 'ba', 'baths', 'full bath', 'half bath'],
    operators: {
      'at least': 'min',
      'minimum': 'min',
      'exactly': 'exact',
      'or more': 'min'
    }
  },

  squareFootage: {
    fields: ['LivingArea', 'BuildingAreaTotal'],
    aliases: ['sqft', 'square feet', 'square footage', 'size', 'living space', 'sq ft'],
    operators: {
      'at least': 'minSqft',
      'minimum': 'minSqft',
      'over': 'minSqft',
      'under': 'maxSqft',
      'less than': 'maxSqft'
    }
  },

  lotSize: {
    fields: ['LotSizeSquareFeet', 'LotSizeArea'],
    aliases: ['lot', 'lot size', 'land', 'yard', 'acreage', 'acre', 'acres'],
    operators: {
      'large': 'minLot',
      'big': 'minLot',
      'spacious': 'minLot',
      'at least': 'minLot'
    }
  },

  // Property types
  propertyType: {
    fields: ['PropertyType', 'PropertyTypeLabel'],
    mappings: {
      'house': ['Detached', 'Single Family Detached'],
      'home': ['Detached', 'Single Family Detached'],
      'single family': ['Detached', 'Single Family Detached'],
      'condo': ['Attached', 'Condominium'],
      'condominium': ['Attached', 'Condominium'],
      'townhome': ['Townhouse'],
      'townhouse': ['Townhouse'],
      'apartment': ['Attached', 'Condominium'],
      'villa': ['Villa'],
      'patio home': ['Patio Home'],
      'manufactured': ['Manufactured Home'],
      'mobile': ['Mobile Home'],
      'land': ['Vacant Land', 'Lots And Land']
    }
  },

  // Location fields
  location: {
    fields: ['City', 'PostalCode', 'MLSAreaMajor', 'SubdivisionName'],
    cities: [
      'Phoenix', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 'Gilbert',
      'Glendale', 'Peoria', 'Surprise', 'Avondale', 'Goodyear', 'Buckeye',
      'Queen Creek', 'Cave Creek', 'Fountain Hills', 'Paradise Valley',
      'Carefree', 'Anthem', 'Litchfield Park', 'Sun City', 'Sun City West'
    ],
    areas: {
      'north phoenix': ['North Phoenix', 'Desert Ridge', 'Deer Valley'],
      'south phoenix': ['South Phoenix', 'Ahwatukee'],
      'east valley': ['Mesa', 'Tempe', 'Chandler', 'Gilbert', 'Queen Creek'],
      'west valley': ['Glendale', 'Peoria', 'Surprise', 'Avondale', 'Goodyear'],
      'downtown': ['Downtown Phoenix', 'Central City'],
      'biltmore': ['Biltmore', 'Arcadia'],
      'old town': ['Old Town Scottsdale']
    }
  },

  // Features
  features: {
    pool: {
      fields: ['PoolYN', 'PoolFeatures'],
      aliases: ['pool', 'swimming pool', 'spa', 'hot tub', 'jacuzzi']
    },
    garage: {
      fields: ['GarageSpaces', 'CoveredSpaces'],
      aliases: ['garage', 'car garage', 'parking', 'carport', 'covered parking']
    },
    fireplace: {
      fields: ['FireplaceYN', 'FireplaceFeatures'],
      aliases: ['fireplace', 'wood burning', 'gas fireplace']
    },
    view: {
      fields: ['ViewFeatures'],
      aliases: ['view', 'mountain view', 'city view', 'golf course view', 'water view']
    },
    waterfront: {
      fields: ['WaterfrontFeatures'],
      aliases: ['waterfront', 'lakefront', 'water access', 'canal']
    }
  },

  // School information
  schools: {
    fields: ['ElementarySchool', 'MiddleOrJuniorSchool', 'HighSchool', 'SchoolDistrict'],
    aliases: ['school', 'schools', 'school district', 'elementary', 'middle school', 'high school'],
    topDistricts: [
      'Scottsdale Unified',
      'Gilbert Public Schools',
      'Chandler Unified',
      'Paradise Valley Unified',
      'Deer Valley Unified',
      'Peoria Unified',
      'BASIS'
    ]
  },

  // HOA and fees
  hoa: {
    fields: ['AssociationFee', 'AssociationFeeFrequency', 'HOAFees'],
    aliases: ['hoa', 'hoa fee', 'association fee', 'monthly fee'],
    operators: {
      'no hoa': 'noHOA',
      'low hoa': 'maxHOA',
      'under': 'maxHOA'
    }
  },

  // Age and condition
  yearBuilt: {
    fields: ['YearBuilt'],
    aliases: ['built', 'year built', 'age', 'construction year'],
    operators: {
      'new': 'minYear',
      'newer than': 'minYear',
      'built after': 'minYear',
      'older than': 'maxYear',
      'built before': 'maxYear'
    }
  },

  // Listing status and timing
  status: {
    fields: ['StandardStatus', 'MlsStatus'],
    aliases: ['status', 'active', 'pending', 'sold'],
    values: {
      'active': 'Active',
      'coming soon': 'Coming Soon',
      'pending': 'Pending',
      'under contract': 'Pending',
      'sold': 'Closed'
    }
  },

  daysOnMarket: {
    fields: ['DaysOnMarket', 'CumulativeDaysOnMarket', 'ListingContractDate'],
    aliases: ['days on market', 'dom', 'new listing', 'just listed', 'fresh', 'new'],
    operators: {
      'new': 'maxDays',
      'just listed': 'maxDays',
      'fresh': 'maxDays',
      'hot': 'maxDays'
    }
  },

  // Additional features
  interiorFeatures: {
    fields: ['InteriorFeatures'],
    aliases: [
      'granite', 'quartz', 'hardwood', 'tile', 'carpet', 'laminate',
      'stainless steel', 'upgraded', 'renovated', 'remodeled',
      'open floor plan', 'vaulted ceilings', 'skylights'
    ]
  },

  exteriorFeatures: {
    fields: ['ExteriorFeatures', 'ConstructionMaterials'],
    aliases: [
      'stucco', 'brick', 'stone', 'siding', 'block',
      'patio', 'deck', 'balcony', 'courtyard', 'rv gate',
      'desert landscaping', 'grass', 'artificial turf'
    ]
  },

  utilities: {
    fields: ['Cooling', 'Heating', 'Sewer', 'WaterSource'],
    aliases: [
      'ac', 'air conditioning', 'central air', 'heat pump',
      'gas heat', 'electric', 'solar', 'well water', 'city water',
      'septic', 'sewer'
    ]
  }
};

// Natural language patterns for complex queries
export const QueryPatterns = {
  // Price patterns
  pricePatterns: [
    /(\d+)k/gi, // 500k
    /(\d+),?(\d+)/gi, // 500,000 or 500000
    /\$(\d+(?:,\d+)*)/gi, // $500,000
    /(\d+(?:\.\d+)?)\s*m(?:illion)?/gi, // 2.5m or 2.5 million
  ],

  // Size patterns
  sizePatterns: [
    /(\d+(?:,\d+)?)\s*sq(?:uare)?\s*f(?:ee)?t/gi, // 2000 sqft
    /(\d+(?:\.\d+)?)\s*acre/gi, // 0.5 acre
  ],

  // Complex queries
  complexPatterns: {
    'move in ready': ['StandardStatus:Active', 'renovated', 'updated'],
    'investment property': ['rental', 'income', 'multi-family'],
    'luxury': ['price:minPrice:1000000', 'pool', 'view'],
    'starter home': ['price:maxPrice:400000', 'bedrooms:max:3'],
    'family home': ['bedrooms:min:3', 'bathrooms:min:2', 'good schools'],
    'downsizing': ['bedrooms:max:2', 'low maintenance', 'condo OR townhome'],
    'fixer upper': ['price:below market', 'needs work', 'as-is']
  }
};

// Helper functions for AI query processing
export const AIQueryHelpers = {
  // Extract numeric values with units
  extractNumericValue: (text: string, pattern: RegExp): number | null => {
    if (!text) return null;
    const match = text.match(pattern);
    if (!match || !match[1]) return null;
    
    let value = parseFloat(match[1].replace(/,/g, ''));
    if (text.includes('k')) value *= 1000;
    if (text.includes('m') || text.includes('million')) value *= 1000000;
    
    return value;
  },

  // Parse relative dates
  parseRelativeDate: (text: string): number | null => {
    const lowerText = text.toLowerCase();
    if (lowerText.includes('today') || lowerText.includes('just listed')) return 1;
    if (lowerText.includes('this week') || lowerText.includes('new')) return 7;
    if (lowerText.includes('this month')) return 30;
    
    const match = lowerText.match(/last (\d+) days?/);
    if (match) return parseInt(match[1]);
    
    return null;
  },

  // Build filter object from natural language
  buildFilters: (query: string): any => {
    const filters: any = {};
    const lowerQuery = query.toLowerCase();

    // Process each field mapping
    Object.entries(MLSFieldMapping).forEach(([key, mapping]: [string, any]) => {
      if (mapping.aliases) {
        mapping.aliases.forEach((alias: string) => {
          if (lowerQuery.includes(alias)) {
            // Extract value based on operators
            if (mapping.operators) {
              Object.entries(mapping.operators).forEach(([operator, filterKey]) => {
                if (lowerQuery.includes(operator)) {
                  // Extract numeric value after operator
                  const pattern = new RegExp(`${operator}\\s*(\\d+(?:,\\d+)*(?:\\.\\d+)?)[km]?`, 'i');
                  const value = AIQueryHelpers.extractNumericValue(lowerQuery, pattern);
                  if (value) filters[filterKey as string] = value;
                }
              });
            }
          }
        });
      }
    });

    return filters;
  },

  // Generate human-readable response
  generateResponse: (filters: any, resultCount: number): string => {
    let parts = [];

    if (filters.city) parts.push(`in ${filters.city}`);
    if (filters.bedrooms) parts.push(`${filters.bedrooms}+ bedrooms`);
    if (filters.bathrooms) parts.push(`${filters.bathrooms}+ bathrooms`);
    if (filters.maxPrice) parts.push(`under $${filters.maxPrice.toLocaleString()}`);
    if (filters.minPrice) parts.push(`over $${filters.minPrice.toLocaleString()}`);
    if (filters.pool) parts.push('with pool');
    if (filters.minSqft) parts.push(`at least ${filters.minSqft.toLocaleString()} sq ft`);

    const criteria = parts.length > 0 ? parts.join(', ') : 'your criteria';
    
    if (resultCount === 0) {
      return `I couldn't find any properties matching ${criteria}. Try adjusting your search criteria.`;
    } else if (resultCount === 1) {
      return `I found 1 property matching ${criteria}!`;
    } else {
      return `I found ${resultCount} properties matching ${criteria}!`;
    }
  }
};